import { MapContainer, ScaleControl, TileLayer, useMap, ZoomControl } from "react-leaflet"
import { useTranslation } from "react-i18next"
import type { LatLngTuple, Map as LeafletMap } from "leaflet"
import type { FC, PropsWithChildren } from "react"
import { useEffect, useState } from "react"
import { alpha, Box, Typography, useMediaQuery, useTheme } from "@mui/material"

import { OSM_TILE_SERVER_CONFIG } from "features/config"
import { MAP_HEIGHT, DEFAULT_MAP_CENTER } from "helpers/utils/constants"
import ClickIcon from "icons/ClickIcon"
import { ZoomTracker } from "./ZoomTracker"
import "./map.css"

// Enables scroll-wheel zoom only once the map is active, so scrolling the page
// over an inactive map scrolls the page instead of zooming the map.
const ScrollZoomController: FC<{ active: boolean }> = ({ active }) => {
  const map = useMap()
  useEffect(() => {
    if (active) {
      map.scrollWheelZoom.enable()
    } else {
      map.scrollWheelZoom.disable()
    }
  }, [active, map])
  return null
}

/**
 * Leaflet only re-measures on a window resize. A map that fills what a card has
 * left over also changes size when something above it grows, with no window
 * event to catch it, and the tiles end up offset. So watch the container.
 */
const ResizeHandler: FC = () => {
  const map = useMap()
  useEffect(() => {
    const container = map.getContainer()
    const observer = new ResizeObserver(() => map.invalidateSize({ animate: false }))
    observer.observe(container)
    return () => observer.disconnect()
  }, [map])
  return null
}

// Hands the map instance back to the parent, for flyTo and friends.
const MapReadyHandler: FC<{ onMapReady: (map: LeafletMap) => void }> = ({
  onMapReady,
}) => {
  const map = useMap()
  useEffect(() => {
    onMapReady(map)
  }, [map, onMapReady])
  return null
}

interface MapProps {
  center?: LatLngTuple | null
  mapHeight?: string
  initialZoomLevel?: number
  enableInteractionOverlay?: boolean
  // Skips the click-to-interact veil. Set it when the user has already driven
  // the map from outside, e.g. by picking a span in the ribbon.
  activated?: boolean
  // Leaflet's +/− buttons. Off on a short map, where they just cover data.
  showZoomControl?: boolean
  onMapReady?: (map: LeafletMap) => void
}

const Map: FC<PropsWithChildren<MapProps>> = ({
  center,
  mapHeight = MAP_HEIGHT,
  initialZoomLevel = 9,
  enableInteractionOverlay = true,
  activated = false,
  showZoomControl = true,
  onMapReady,
  children,
}) => {
  const { t } = useTranslation()
  const zoomLevel = center ? initialZoomLevel : 2
  const theme = useTheme()
  const [isMapActive, setIsMapActive] = useState(!enableInteractionOverlay)
  const [isHovering, setIsHovering] = useState(false)
  // A capability query, not a breakpoint: a narrow desktop window still has a
  // wheel to protect the page scroll from, a big tablet doesn't.
  const isCoarsePointer = useMediaQuery("(pointer: coarse)")

  // One-way latch, the same as clicking the veil. Touch skips it entirely: the
  // veil exists to protect page scroll from the wheel, which touch doesn't have,
  // and a tap fires `mouseenter` — the veil would appear and eat that tap.
  useEffect(() => {
    if (activated || isCoarsePointer) setIsMapActive(true)
  }, [activated, isCoarsePointer])

  const handleMapClick = () => {
    setIsMapActive(true)
  }

  const handleMouseEnter = () => {
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
  }

  const mapElement = (
    <MapContainer
      center={center ?? DEFAULT_MAP_CENTER}
      zoom={zoomLevel}
      scrollWheelZoom={false}
      // `MapContainer` freezes its options at creation, so the built-in control
      // can't be toggled by a prop. As a child it mounts with the layout.
      zoomControl={false}
      style={{ height: mapHeight }}
    >
      {showZoomControl && <ZoomControl position="topleft" />}
      <ScrollZoomController active={isMapActive} />
      <ResizeHandler />
      {onMapReady && <MapReadyHandler onMapReady={onMapReady} />}
      <ZoomTracker initialZoom={zoomLevel}>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url={OSM_TILE_SERVER_CONFIG}
        />
        {children}
        <ScaleControl position="bottomleft" />
      </ZoomTracker>
    </MapContainer>
  )

  if (!enableInteractionOverlay) {
    return mapElement
  }

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: "relative", height: mapHeight, width: "100%" }}
    >
      {mapElement}
      {!isCoarsePointer && !isMapActive && isHovering && (
        <Box
          onClick={handleMapClick}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: "blur(2px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            zIndex: 1000,
            pointerEvents: "auto",
            backgroundColor: alpha(theme.palette.grey[100], 0.4),
            animation: "fadeIn 0.3s ease-in",
            "@keyframes fadeIn": {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "background.paper",
              px: 3,
              py: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.neutral[400]}`,
            }}
          >
            <ClickIcon color={theme.palette.primary.main} />
            <Typography variant="body2" color="text.primary" fontWeight={500}>
              {t(`geo_info.CLICK_TO_INTERACT_WITH_MAP`)}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default Map
